import React, { useState, useEffect, useRef } from "react";
import { FiSend, FiRefreshCw } from "react-icons/fi";
import DoctorAgentService, {
  AgentResponse,
  AwaitingReviewResponse,
  Message as DBMessage,
} from "../../services/doctorAgentService";
import ReviewModal from "../../shared/components/ReviewModal";
import MarkdownRenderer from "../../shared/components/MarkdownRenderer";

interface ChatBoxProps {
  threadId: string | null;
  onThreadCreated?: (threadId: string) => void;
}

interface Message {
  id: string;
  content: string;
  sender: "user" | "agent";
  timestamp: Date;
  status?: "sending" | "sent" | "error";
  messageType?: "message" | "interrupt" | "decision";
}

const ChatBox: React.FC<ChatBoxProps> = ({ threadId, onThreadCreated }) => {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(
    threadId
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMessages, setIsFetchingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);

  // Review modal state
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [pendingReview, setPendingReview] =
    useState<AwaitingReviewResponse | null>(null);
  const [isProcessingReview, setIsProcessingReview] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /**
   * Convert database messages to UI messages
   */
  const convertDBMessagesToUI = (dbMessages: DBMessage[]): Message[] => {
    console.log("DB Messages:", dbMessages);
    return dbMessages.map((msg) => ({
      id: `db-${msg.message_id}`,
      content: msg.content,
      sender: msg.role === "user" ? "user" : "agent",
      timestamp: new Date(msg.created_at),
      status: "sent" as const,
      messageType: msg.message_type,
    }));
  };

  /**
   * Fetch messages for the current thread
   */
  const fetchThreadMessages = async (tid: string) => {
    try {
      setIsFetchingMessages(true);
      setError(null);
      const threadDetail = await DoctorAgentService.getThreadDetail(tid);
      const uiMessages = convertDBMessagesToUI(threadDetail.messages);
      setMessages(uiMessages);
    } catch (err: any) {
      console.error("Failed to fetch thread messages:", err);
      const errorMessage =
        err?.error || err?.message || "Failed to load conversation history.";
      setError(errorMessage);
      // If thread not found, clear it
      if (err?.error?.includes("not found")) {
        setCurrentThreadId(null);
        setMessages([]);
      }
    } finally {
      setIsFetchingMessages(false);
    }
  };

  /**
   * Handle thread change
   */
  useEffect(() => {
    if (threadId) {
      setCurrentThreadId(threadId);
      fetchThreadMessages(threadId);
    } else {
      // New conversation
      setCurrentThreadId(null);
      setMessages([]);
      setError(null);
    }
  }, [threadId]);

  /**
   * Add a message to the chat
   */
  const addMessage = (
    content: string,
    sender: "user" | "agent",
    status: "sending" | "sent" | "error" = "sent"
  ) => {
    const newMessage: Message = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      content,
      sender,
      timestamp: new Date(),
      status,
    };
    setMessages((prev) => [...prev, newMessage]);
    return newMessage;
  };

  /**
   * Update message status
   */
  const updateMessageStatus = (
    messageId: string,
    status: "sending" | "sent" | "error"
  ) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === messageId ? { ...msg, status } : msg))
    );
  };

  /**
   * Handle agent response
   */
  const handleAgentResponse = (response: AgentResponse) => {
    console.log("=".repeat(80));
    console.log("🔍 HANDLE AGENT RESPONSE - Frontend received response:");
    console.log("=".repeat(80));
    console.log("Response status:", response.status);
    console.log("Full response:", JSON.stringify(response, null, 2));
    console.log("=".repeat(80));

    // Clear waiting state when we get any response
    setIsWaitingForResponse(false);

    if (response.status === "final") {
      console.log("✅ Status is FINAL - Adding agent message to chat");
      // Add agent's final response to chat
      addMessage(response.content, "agent", "sent");
      // Clear review processing state
      setIsProcessingReview(false);
    } else if (response.status === "awaiting_review") {
      console.log("🛑 Status is AWAITING_REVIEW - Showing ReviewModal");
      console.log("Review prompt:", response.review_prompt);
      console.log("Proposed args:", response.proposed_args);
      console.log("Run ID:", response.run_id);

      // Show review modal (could be a chained review)
      setPendingReview(response);
      setShowReviewModal(true);
      setIsProcessingReview(false);

      // Add a placeholder message
      addMessage(
        "I need your approval to proceed. Please review the proposed action.",
        "agent",
        "sent"
      );
    }
  };

  /**
   * Send a message to the agent
   */
  const handleSendMessage = async () => {
    if (!message.trim() || isLoading) return;

    const userMessage = message.trim();
    setMessage("");
    setError(null);

    // Add user message to chat
    const sentMessage = addMessage(userMessage, "user", "sending");

    try {
      setIsLoading(true);
      setIsWaitingForResponse(true);
      updateMessageStatus(sentMessage.id, "sent");

      console.log("📤 Sending message to backend:", userMessage);
      console.log("Thread ID:", currentThreadId);

      // Send message to agent (will create thread if needed)
      const response = await DoctorAgentService.sendMessage({
        message: userMessage,
        thread_id: currentThreadId || undefined,
      });

      console.log("📥 Received response from backend:");
      console.log(JSON.stringify(response, null, 2));

      // If this is a new thread, update the thread ID
      if (!currentThreadId && response.thread_id) {
        console.log("🆕 New thread created:", response.thread_id);
        setCurrentThreadId(response.thread_id);
        onThreadCreated?.(response.thread_id);
      }

      handleAgentResponse(response);
    } catch (err: any) {
      console.error("Failed to send message:", err);
      updateMessageStatus(sentMessage.id, "error");
      setIsWaitingForResponse(false);
      const errorMessage =
        err?.error ||
        err?.message ||
        "Failed to send message. Please try again.";
      setError(errorMessage);
      addMessage(`Error: ${errorMessage}`, "agent", "error");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * Handle Accept decision
   */
  const handleAccept = async () => {
    if (!pendingReview) return;

    try {
      setIsLoading(true);
      setIsProcessingReview(true);
      setShowReviewModal(false); // Close modal immediately

      const response = await DoctorAgentService.makeDecision({
        thread_id: pendingReview.thread_id,
        run_id: pendingReview.run_id,
        decision: "accept",
      });

      setPendingReview(null);
      handleAgentResponse(response);
    } catch (err: any) {
      console.error("Failed to accept:", err);
      const errorMessage =
        err?.error || err?.message || "Failed to process decision.";
      setError(errorMessage);
      addMessage(`Error: ${errorMessage}`, "agent", "error");
      setIsProcessingReview(false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle Reject decision
   */
  const handleReject = async (reason: string) => {
    if (!pendingReview) return;

    try {
      setIsLoading(true);
      setIsProcessingReview(true);
      setShowReviewModal(false); // Close modal immediately

      const response = await DoctorAgentService.makeDecision({
        thread_id: pendingReview.thread_id,
        run_id: pendingReview.run_id,
        decision: "reject",
        reason,
      });

      setPendingReview(null);
      handleAgentResponse(response);
    } catch (err: any) {
      console.error("Failed to reject:", err);
      const errorMessage =
        err?.error || err?.message || "Failed to process decision.";
      setError(errorMessage);
      addMessage(`Error: ${errorMessage}`, "agent", "error");
      setIsProcessingReview(false);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle Edit decision
   */
  const handleEdit = async (args: Record<string, any>) => {
    if (!pendingReview) return;

    try {
      setIsLoading(true);
      setIsProcessingReview(true);
      setShowReviewModal(false); // Close modal immediately

      const response = await DoctorAgentService.makeDecision({
        thread_id: pendingReview.thread_id,
        run_id: pendingReview.run_id,
        decision: "edit",
        args,
      });

      // If still awaiting review, update the pending review and show modal again
      if (response.status === "awaiting_review") {
        setPendingReview(response);
        setShowReviewModal(true);
        setIsProcessingReview(false);
        addMessage(
          "I've updated the proposed changes. Please review again.",
          "agent",
          "sent"
        );
      } else {
        setPendingReview(null);
        handleAgentResponse(response);
      }
    } catch (err: any) {
      console.error("Failed to edit:", err);
      const errorMessage =
        err?.error || err?.message || "Failed to process decision.";
      setError(errorMessage);
      addMessage(`Error: ${errorMessage}`, "agent", "error");
      setIsProcessingReview(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-col h-full w-full rounded-2xl bg-white shadow-lg border border-gray-100">
        {/* Chat Window */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Header - Modern Status Bar */}
          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 via-white to-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                <span className="text-sm font-medium text-gray-700">
                  {isLoading || isFetchingMessages || isWaitingForResponse
                    ? "AI is thinking..."
                    : "Ready to assist"}
                </span>
              </div>
              {currentThreadId && (
                <button
                  onClick={() =>
                    currentThreadId && fetchThreadMessages(currentThreadId)
                  }
                  disabled={isFetchingMessages}
                  className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all disabled:opacity-50 group"
                  title="Refresh messages"
                >
                  <FiRefreshCw
                    className={`w-4 h-4 transition-transform group-hover:rotate-180 ${
                      isFetchingMessages ? "animate-spin" : ""
                    }`}
                  />
                </button>
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30 p-6 overflow-y-auto flex flex-col gap-4 min-h-0 relative">
            {/* Loading Overlay for Review Processing */}
            {isProcessingReview && (
              <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center z-10">
                <div className="bg-white rounded-2xl shadow-xl p-6 flex flex-col items-center gap-4 max-w-sm">
                  <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full" />
                  <div className="text-center">
                    <p className="text-gray-900 font-semibold mb-1">
                      Processing Review Decision
                    </p>
                    <p className="text-gray-500 text-sm">
                      Please wait while the agent processes your response...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isFetchingMessages && messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <div className="relative">
                  <div className="animate-spin w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full" />
                  <div className="absolute inset-0 animate-ping w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full opacity-20" />
                </div>
                <p className="text-gray-600 mt-4 font-medium">Loading conversation...</p>
                <p className="text-gray-400 text-sm mt-1">Please wait</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-3xl shadow-sm border border-blue-100 max-w-md">
                  <div className="bg-white p-4 rounded-2xl shadow-sm mb-4 inline-block">
                    <svg
                      className="w-16 h-16 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                      />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {currentThreadId
                      ? "No messages yet"
                      : "Start a conversation"}
                  </h3>
                  <p className="text-gray-600">
                    Ask me to help you with OpenEMR operations
                  </p>
                  <div className="mt-4 text-left bg-white/50 p-3 rounded-lg">
                    <p className="text-xs text-gray-500 font-medium mb-1">Try asking:</p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li>• Get patient details</li>
                      <li>• Create an appointment</li>
                      <li>• Add a SOAP note</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={msg.id}
                  className={`flex ${
                    msg.sender === "user" ? "justify-end" : "justify-start"
                  } animate-fadeIn`}
                  style={{
                    animationDelay: `${index * 0.05}s`,
                  }}
                >
                  <div
                    className={`group px-6 py-4 rounded-2xl max-w-[80%] transition-all duration-300 ${
                      msg.sender === "user"
                        ? "bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-lg hover:shadow-xl hover:scale-[1.02]"
                        : msg.status === "error"
                        ? "bg-gradient-to-br from-red-50 to-red-100 text-red-900 border-2 border-red-200 shadow-md"
                        : "bg-white text-gray-900 shadow-md border border-gray-100 hover:shadow-lg hover:border-blue-100 transition-all"
                    }`}
                  >
                    {/* Message Content */}
                    <MarkdownRenderer
                      content={msg.content}
                      compact
                      variant={msg.sender}
                    />

                    {/* Status Indicators */}
                    <div className="flex items-center gap-2 mt-2">
                      {msg.status === "sending" && (
                        <div className="flex items-center gap-2 text-xs opacity-70">
                          <div className="w-1.5 h-1.5 bg-current rounded-full animate-pulse" />
                          <span>Sending...</span>
                        </div>
                      )}
                      {msg.messageType && msg.messageType !== "message" && (
                        <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-black/5">
                          {msg.messageType === "interrupt" ? (
                            <>
                              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full animate-pulse" />
                              <span>Awaiting approval</span>
                            </>
                          ) : (
                            <>
                              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                              <span>Decision made</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Loading indicator while waiting for agent response */}
            {isWaitingForResponse && (
              <div className="flex justify-start animate-fadeIn">
                <div className="px-6 py-4 rounded-2xl max-w-[80%] shadow-md bg-white text-gray-900 border border-blue-100">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="animate-spin w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full" />
                      <div className="absolute inset-0 animate-ping w-5 h-5 border-2 border-blue-400 rounded-full opacity-20" />
                    </div>
                    <div className="flex gap-1">
                      <span className="text-sm font-medium text-gray-700">AI is thinking</span>
                      <span className="animate-bounce delay-0">.</span>
                      <span className="animate-bounce delay-100">.</span>
                      <span className="animate-bounce delay-200">.</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Error Display */}
          {error && (
            <div className="mx-4 mb-2 px-4 py-3 bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg shadow-sm animate-fadeIn">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">Error</p>
                  <p className="text-sm text-red-700 mt-0.5">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="p-4 border-t border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex items-end gap-3">
              <div className="flex-1 relative group">
                <textarea
                  placeholder={
                    isProcessingReview
                      ? "Processing review decision..."
                      : isWaitingForResponse
                      ? "Waiting for AI response..."
                      : "Type your message... (Shift+Enter for new line)"
                  }
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-white resize-none outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all min-h-[48px] max-h-32 placeholder:text-gray-400"
                  rows={1}
                  disabled={
                    isLoading || isProcessingReview || isWaitingForResponse
                  }
                  style={{
                    height: "auto",
                    minHeight: "48px",
                    maxHeight: "128px",
                  }}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = "auto";
                    target.style.height =
                      Math.min(target.scrollHeight, 128) + "px";
                  }}
                />
                {/* Character indicator for long messages */}
                {message.length > 100 && (
                  <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white px-2 py-1 rounded">
                    {message.length}
                  </div>
                )}
              </div>
              <button
                className="group p-3 bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg flex-shrink-0 active:scale-95 transform"
                onClick={handleSendMessage}
                disabled={
                  isLoading ||
                  isFetchingMessages ||
                  isProcessingReview ||
                  isWaitingForResponse ||
                  !message.trim()
                }
                aria-label="Send message"
                title="Send message (Enter)"
              >
                <FiSend className="w-5 h-5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </button>
            </div>
            {/* Help text */}
            <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
              <span>Press Enter to send, Shift+Enter for new line</span>
              {(isLoading || isWaitingForResponse) && (
                <span className="flex items-center gap-1.5 text-blue-600">
                  <div className="w-1.5 h-1.5 bg-blue-600 rounded-full animate-pulse" />
                  Processing
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Review Modal */}
      {pendingReview && (
        <ReviewModal
          isOpen={showReviewModal}
          onClose={() => setShowReviewModal(false)}
          reviewPrompt={pendingReview.review_prompt}
          proposedArgs={pendingReview.proposed_args}
          onAccept={handleAccept}
          onReject={handleReject}
          onEdit={handleEdit}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default ChatBox;
